import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Save, X, Tag, Users, Eye, EyeOff } from "lucide-react";
import { api } from "@/lib/api";

const EMPTY_CAT = { name: "", slug: "", description: "", color: "", menu_label: "", menu_visible: true, active: true };
const EMPTY_AUTH = { name: "", slug: "", photo_url: "", bio: "", role: "", twitter: "", instagram: "", facebook: "", linkedin: "", email: "", active: true };

export default function AdminCatalogTab() {
  const [sub, setSub] = useState("categories");
  return (
    <div data-testid="admin-catalog-tab">
      <div className="flex gap-1 mb-6 border-b" style={{ borderColor: "var(--border)" }}>
        {[
          { k: "categories", label: "Categorías / Menú", icon: Tag },
          { k: "authors", label: "Autores", icon: Users },
        ].map(({ k, label, icon: Icon }) => (
          <button
            key={k}
            onClick={() => setSub(k)}
            data-testid={`admin-catalog-sub-${k}`}
            className="px-4 py-2 text-xs uppercase tracking-widest font-bold flex items-center gap-2 relative"
            style={{ color: sub === k ? "var(--accent)" : "var(--text-muted)" }}
          >
            <Icon size={13} /> {label}
            {sub === k && <span className="absolute left-2 right-2 -bottom-[1px] h-[2px]" style={{ background: "var(--accent)" }} />}
          </button>
        ))}
      </div>
      {sub === "categories" ? <CategoriesPanel /> : <AuthorsPanel />}
    </div>
  );
}

function CategoriesPanel() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(null);

  const reload = async () => {
    const r = await api.get("/admin/categories");
    setItems(r.data || []);
  };
  useEffect(() => { reload(); }, []);

  const save = async () => {
    try {
      if (draft.id) await api.put(`/admin/categories/${draft.id}`, draft);
      else await api.post("/admin/categories", draft);
      toast.success("Categoría guardada");
      setDraft(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };
  const del = async (id) => {
    if (!window.confirm("¿Eliminar categoría? (si tiene artículos publicados no se podrá)")) return;
    try {
      await api.delete(`/admin/categories/${id}`);
      toast.success("Eliminada");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };
  const move = async (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    await api.post("/admin/categories/reorder", { order: next.map((c) => c.id) });
    toast.success("Orden guardado");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-headline uppercase text-3xl md:text-4xl flex items-center gap-2"><Tag size={26} /> Categorías y menú</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Creá, renombrá, ordená y controlá visibilidad del menú del sitio.</p>
        </div>
        <button onClick={() => setDraft({ ...EMPTY_CAT })} className="btn-accent" data-testid="admin-cat-new">
          <Plus size={16} /> Nueva
        </button>
      </div>

      {items.length === 0 ? (
        <p className="text-center py-12 text-sm" style={{ color: "var(--text-muted)" }}>Sin categorías. Agregá la primera.</p>
      ) : (
        <div className="space-y-2">
          {items.map((c, idx) => (
            <div key={c.id} className="border rounded-lg p-3 flex items-center gap-3 flex-wrap" style={{ borderColor: "var(--border)", opacity: c.active === false ? 0.5 : 1 }} data-testid={`admin-cat-row-${c.id}`}>
              <div className="flex flex-col text-[10px] opacity-70">
                <button onClick={() => move(idx, -1)} className="hover:opacity-100 px-1">▲</button>
                <button onClick={() => move(idx, 1)} className="hover:opacity-100 px-1">▼</button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-base">{c.name}</span>
                  <span className="text-[10px] opacity-60">/{c.slug}</span>
                  {c.menu_visible === false && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 font-bold">OCULTA EN MENÚ</span>
                  )}
                  {c.active === false && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-bold">DESACTIVADA</span>
                  )}
                </div>
                {c.menu_label && c.menu_label !== c.name && (
                  <p className="text-xs opacity-70">Menú: {c.menu_label}</p>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setDraft(c)} className="text-xs px-2 py-1.5 rounded border font-bold" style={{ borderColor: "var(--border)" }} data-testid={`admin-cat-edit-${c.id}`}><Edit size={12} /></button>
                <button onClick={() => del(c.id)} className="text-xs px-2 py-1.5 rounded border font-bold" style={{ borderColor: "var(--border)" }} data-testid={`admin-cat-delete-${c.id}`}><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {draft && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDraft(null)}>
          <div className="bg-[var(--bg)] max-w-lg w-full rounded-lg p-5 space-y-3 border max-h-[90vh] overflow-y-auto" style={{ borderColor: "var(--border)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-headline uppercase text-xl">{draft.id ? "Editar" : "Nueva"} categoría</h3>
              <button onClick={() => setDraft(null)}><X size={18} /></button>
            </div>
            <input type="text" placeholder="Nombre" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="w-full px-3 py-2 border rounded bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="admin-cat-name" />
            <input type="text" placeholder="Slug (opcional)" value={draft.slug || ""} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} className="w-full px-3 py-2 border rounded bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
            <input type="text" placeholder="Etiqueta en menú (opcional)" value={draft.menu_label || ""} onChange={(e) => setDraft({ ...draft, menu_label: e.target.value })} className="w-full px-3 py-2 border rounded bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
            <textarea placeholder="Descripción (opcional)" value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="w-full px-3 py-2 border rounded bg-transparent text-sm min-h-[60px]" style={{ borderColor: "var(--border)" }} />
            <div className="flex gap-3 items-center">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={draft.active !== false} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} /> Activa
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={draft.menu_visible !== false} onChange={(e) => setDraft({ ...draft, menu_visible: e.target.checked })} data-testid="admin-cat-menu-visible" /> Visible en menú
              </label>
            </div>
            <button onClick={save} className="btn-accent w-full" data-testid="admin-cat-save"><Save size={14} /> Guardar</button>
          </div>
        </div>
      )}
    </div>
  );
}

function AuthorsPanel() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(null);

  const reload = async () => {
    const r = await api.get("/admin/authors");
    setItems(r.data || []);
  };
  useEffect(() => { reload(); }, []);

  const save = async () => {
    try {
      if (draft.id) await api.put(`/admin/authors/${draft.id}`, draft);
      else await api.post("/admin/authors", draft);
      toast.success("Autor guardado");
      setDraft(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };
  const toggleActive = async (a) => {
    try {
      await api.put(`/admin/authors/${a.id}`, { ...a, active: !a.active });
      reload();
    } catch (e) { toast.error("Error"); }
  };
  const del = async (id) => {
    if (!window.confirm("¿Desactivar autor? (los artículos conservan la atribución)")) return;
    await api.delete(`/admin/authors/${id}`);
    toast.success("Autor desactivado");
    reload();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-headline uppercase text-3xl md:text-4xl flex items-center gap-2"><Users size={26} /> Autores</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Perfiles de autores para artículos regulares — nombre, foto, biografía y redes.</p>
        </div>
        <button onClick={() => setDraft({ ...EMPTY_AUTH })} className="btn-accent" data-testid="admin-author-new">
          <Plus size={16} /> Nuevo
        </button>
      </div>

      {items.length === 0 ? (
        <p className="text-center py-12 text-sm" style={{ color: "var(--text-muted)" }}>Sin autores. Agregá el primero.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((a) => (
            <div key={a.id} className="border rounded-lg p-3 flex gap-3" style={{ borderColor: "var(--border)", opacity: a.active === false ? 0.55 : 1 }} data-testid={`admin-author-row-${a.id}`}>
              <img src={a.photo_url || a.avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(a.name)}`} alt={a.name} className="w-14 h-14 rounded-full object-cover flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold">{a.name}</span>
                  <span className="text-[10px] opacity-60">/{a.slug}</span>
                  {a.role && <span className="text-[10px] px-1.5 py-0.5 rounded bg-pink-100 text-pink-700 font-bold">{a.role}</span>}
                  {a.active === false && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 font-bold">INACTIVO</span>}
                </div>
                {a.bio && <p className="text-xs opacity-70 line-clamp-2 mt-1">{a.bio}</p>}
                <div className="flex items-center gap-1 mt-2">
                  <button onClick={() => setDraft(a)} className="text-xs px-2 py-1 rounded border font-bold" style={{ borderColor: "var(--border)" }} data-testid={`admin-author-edit-${a.id}`}><Edit size={11} /></button>
                  <button onClick={() => toggleActive(a)} className="text-xs px-2 py-1 rounded border font-bold" style={{ borderColor: "var(--border)" }} title={a.active === false ? "Activar" : "Desactivar"}>
                    {a.active === false ? <Eye size={11} /> : <EyeOff size={11} />}
                  </button>
                  <button onClick={() => del(a.id)} className="text-xs px-2 py-1 rounded border font-bold" style={{ borderColor: "var(--border)" }} data-testid={`admin-author-delete-${a.id}`}><Trash2 size={11} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {draft && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDraft(null)}>
          <div className="bg-[var(--bg)] max-w-xl w-full rounded-lg p-5 space-y-3 border max-h-[90vh] overflow-y-auto" style={{ borderColor: "var(--border)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-headline uppercase text-xl">{draft.id ? "Editar" : "Nuevo"} autor</h3>
              <button onClick={() => setDraft(null)}><X size={18} /></button>
            </div>
            <input type="text" placeholder="Nombre completo" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="w-full px-3 py-2 border rounded bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="admin-author-name" />
            <input type="text" placeholder="Rol (ej: Periodista, Editor)" value={draft.role || ""} onChange={(e) => setDraft({ ...draft, role: e.target.value })} className="w-full px-3 py-2 border rounded bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
            <input type="url" placeholder="URL de la foto" value={draft.photo_url || ""} onChange={(e) => setDraft({ ...draft, photo_url: e.target.value })} className="w-full px-3 py-2 border rounded bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
            <textarea placeholder="Biografía corta" value={draft.bio || ""} onChange={(e) => setDraft({ ...draft, bio: e.target.value })} className="w-full px-3 py-2 border rounded bg-transparent text-sm min-h-[80px]" style={{ borderColor: "var(--border)" }} />
            <div className="grid grid-cols-2 gap-2">
              <input type="text" placeholder="Twitter / X (sin @)" value={draft.twitter || ""} onChange={(e) => setDraft({ ...draft, twitter: e.target.value })} className="px-3 py-2 border rounded bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
              <input type="text" placeholder="Instagram (sin @)" value={draft.instagram || ""} onChange={(e) => setDraft({ ...draft, instagram: e.target.value })} className="px-3 py-2 border rounded bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
              <input type="text" placeholder="Facebook (handle)" value={draft.facebook || ""} onChange={(e) => setDraft({ ...draft, facebook: e.target.value })} className="px-3 py-2 border rounded bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
              <input type="text" placeholder="LinkedIn (handle)" value={draft.linkedin || ""} onChange={(e) => setDraft({ ...draft, linkedin: e.target.value })} className="px-3 py-2 border rounded bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
            </div>
            <input type="email" placeholder="Email de contacto" value={draft.email || ""} onChange={(e) => setDraft({ ...draft, email: e.target.value })} className="w-full px-3 py-2 border rounded bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.active !== false} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} /> Activo
            </label>
            <button onClick={save} className="btn-accent w-full" data-testid="admin-author-save"><Save size={14} /> Guardar</button>
          </div>
        </div>
      )}
    </div>
  );
}
